import React, { useState, Fragment } from 'react';
import { useTranslate } from 'react-admin';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { CreateButton, EditButton, H2, Drawer } from '~/components';
import { EditForm } from '~/components/ra';
import { Business } from '~/types';
import DocumentCardFields from '~/components/DocumentCardFields';

import palette from '~/theme/palette';
import { useEditMode } from '~/hooks/useEditModeContext';

interface Props {
  record: Business | undefined;
  actionsId?: string; // used to apply some css from BusinessShow
  index?: number;
  edit?: boolean;
  primary?: boolean;
  closeParentDrawer?: () => void;
  permissions: string[];
}

const AddOrEditDocument = (props: Props) => {
  const {
    record,
    index,
    edit,
    primary,
    actionsId = 'section-actions',
    closeParentDrawer,
    permissions,
  } = props;
  const [open, setOpen] = useState(false);
  const label = (key: string): string => `resources.businesses.fields.${key}`;
  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (closeParentDrawer) {
      closeParentDrawer();
    }
  };
  const { editMode } = useEditMode();

  const lastDocIndex = index || (!edit && record?.documents?.length) || 0;
  const getSource = (source: string): string =>
    primary ? `primaryDocument.${source}` : `documents.[${lastDocIndex}].${source}`;

  if (!record) return null;

  return (
    <Fragment>
      <div id={actionsId}>
        {edit ? (
          <EditButton
            onClick={handleOpen}
            disabled={
              !permissions?.includes('business.update') && !permissions?.includes('business.create')
            }
          />
        ) : (
          <CreateButton
            onClick={handleOpen}
            variant='add'
            disabled={!permissions?.includes('business.update')}
          />
        )}
      </div>
      <Drawer
        heading={
          <Box>
            <H2 style={{ margin: 0 }}>{t(edit ? 'documents.edit' : 'documents.add')}</H2>
            {edit &&
              (primary
                ? record.primaryDocument?.id
                : (record.documents || [])[lastDocIndex]?.id) && (
                <Typography variant='subtitle1' style={{ color: palette.text.secondary }}>
                  ID {primary ? record.primaryDocument?.id : record.documents![lastDocIndex].id}
                </Typography>
              )}
          </Box>
        }
        open={open}
        onClose={handleClose}
        editMode={editMode}
      >
        <EditForm
          resource='businesses'
          record={record}
          closeParent={handleClose}
          withSaveConfirmation={false}
        >
          <DocumentCardFields
            getSource={getSource}
            edit={edit}
            primary={primary}
            documentForm='BUSINESS'
            solutionId={record?.solutionId}
          />
        </EditForm>
      </Drawer>
    </Fragment>
  );
};

export default AddOrEditDocument;
